import React, { createContext, useContext, useState } from "react";
import axiosInstance from "../Config/axios";
import Swal from "sweetalert2";

const SummerCampContext = createContext(null);

export const useSummerCamp = () => {
  const context = useContext(SummerCampContext);
  if (!context) {
    throw new Error("useSummerCamp must be used within a SummerCampProvider");
  }
  return context;
};

export const SummerCampProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summerCampList, setSummerCampList] = useState([]);
  const [isDeleting, setDeleting] = useState(false);
  const [isRedirecting, setRedirecting] = useState(false);
  const [dataVpayment, setDataVpayment] = useState(null);

  const addToCartOffering = async (payload) => {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Unauthorized",
        text: "Please login to continue",
      });
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post(
        "/services/offering-cart/",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      Swal.fire({
        icon: "success",
        title: "Added to Cart!",
        text: "Your session has been added to the cart successfully.",
        showConfirmButton: true,
        confirmButtonText: "Go to Cart",
        showCancelButton: true,
        cancelButtonText: "Continue Shopping",
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/cart";
        }
        if (result.isDismissed) {
          window.location.href = "/basketball-summer-camps-details";
        }
      });
      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.detail || "Failed to add offering to cart";

      setError(message);

      Swal.fire({
        icon: "error",
        title: "Add to Cart Failed",
        text: message,
      });

      return null;
    } finally {
      setLoading(false);
    }
  };

  const getOfferingList = async () => {
    const token = localStorage.getItem("accessToken");

    try {
      setError(null);
      const response = await axiosInstance.get("/services/get-offering/", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setSummerCampList(response.data);
      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.message || "Failed to fetch offerings";
      setError(message);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
      return null;
    }
  };

  const getOfferingCart = async () => {
    const token = localStorage.getItem("accessToken");

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get("/services/get-offering-cart/", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (Array.isArray(response?.data)) {
        setSummerCampList(response.data);
        return response.data;
      }
      setSummerCampList([]);
      return [];
    } catch (err) {
      const message =
        err.response?.data?.message || "Failed to fetch offering cart";
      setError(message);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteOfferingById = async (id) => {
    const token = localStorage.getItem("accessToken");

    try {
      setDeleting(true);
      setError(null);

      const response = await axiosInstance.delete(
        `services/cart/offering/remove/?cart_id=${id}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      Swal.fire({
        icon: "success",
        title: "Deleted from Cart!",
        text: "Your item has been deleted from the cart successfully.",
        showConfirmButton: true,
        confirmButtonText: "Go to Cart",
        showCancelButton: true,
        cancelButtonText: "Continue Shopping",
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/cart";
        }
      });
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to delete";
      setError(message);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
      return null;
    } finally {
      setDeleting(false);
    }
  };

  const applyCampCoupon = async (payload) => {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Unauthorized",
        text: "Please login to apply coupons",
      });
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Payload expects: { "cart_id": 30, "code": "CouponCode" }
      const response = await axiosInstance.post(
        "/services/apply-camp-coupon/",
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      Swal.fire({
        icon: "success",
        title: "Coupon Applied!",
        text: `You saved $${response.data.discount_value ? response.data.discount_value : response.data.discounted_amount}!`,
        timer: 2000,
        showConfirmButton: false,
      });

      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Invalid Coupon Code";
      setError(message);

      // Return error structure so UI can handle message display
      return { success: false, message: message };
    } finally {
      setLoading(false);
    }
  };

  const offeringCheckout = async (cartId, coupon) => {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Unauthorized",
        text: "Please login to continue",
      });
      return;
    }

    try {
      setRedirecting(true);
      setError(null);

      const response = await axiosInstance.post(
        "/services/offering-checkout/",
        { cart_id: cartId, coupon_code: coupon },

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const checkoutUrl = response?.data?.url;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error("Checkout URL not found");
      }
    } catch (err) {
      const message =
        err.response?.data?.detail || "Checkout failed. Please try again.";
      setError(message);
      Swal.fire({
        icon: "error",
        title: "Checkout Failed",
        text: message,
      });
    } finally {
      setRedirecting(false);
    }
  };

  const removeCampCoupon = async (cartId) => {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Unauthorized",
        text: "Please login to continue",
      });
      return null;
    }

    try {
      // setLoading(true);
      setError(null);

      const response = await axiosInstance.post(
        "/services/offering/remove-coupon/",
        { cart_id: cartId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.fire({
        icon: "success",
        title: "Coupon Removed",
        text: response.data.message || "Coupon removed successfully",
        timer: 2000,
        showConfirmButton: false,
      });

      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Failed to remove coupon";

      setError(message);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });

      return null;
    } finally {
      // setLoading(false);
    }
  };

  const verifyPayment = async (id) => {
    const token = localStorage.getItem("accessToken");

    try {
      const response = await axiosInstance.get(
        `/payment/verify_payment/?session_id=${id}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (response?.data) {
        setDataVpayment(response.data);
        return response.data;
      }

      return [];
    } catch (err) {
      setDataVpayment(null);
      const message = err.response?.data?.message || "Failed to Verify Payment";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const summercamplistfordashboard = async () => {
    const token = localStorage.getItem("accessToken");

    try {
      const response = await axiosInstance.get(
        `/services/user/offering-registrations`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      return response.data;
    } catch (err) {
      const message = err.response?.data?.message || "Failed to Verify Payment";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const summercampfordashboardbyid = async (id) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axiosInstance.get(
        `/services/user/offering-registrations/${id}/`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      return response.data;
    } catch (err) {
      const message = err.response?.data?.message || "Failed to Verify Payment";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };
  return (
    <SummerCampContext.Provider
      value={{
        loading,
        error,
        addToCartOffering,
        getOfferingList,
        summerCampList,
        getOfferingCart,
        deleteOfferingById,
        isDeleting,
        offeringCheckout,
        applyCampCoupon,
        isRedirecting,
        removeCampCoupon,
        verifyPayment,
        dataVpayment,
        summercamplistfordashboard,
        summercampfordashboardbyid,
      }}
    >
      {children}
    </SummerCampContext.Provider>
  );
};
